import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';
import LegalPageDialog from './LegalPageDialog';
import { Scale, Mail, Phone, MapPin, ArrowUp, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const { t, direction } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0A1628] relative overflow-hidden">
      {/* Top Border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent" />
      
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Brand Section */}
          <div className={`lg:col-span-1 ${direction === 'rtl' ? 'text-right' : ''}`}>
            <div className={`flex items-center gap-3 mb-6 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <Logo size="small" />
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                  {direction === 'rtl' ? 'د. قاسم حسين' : 'Dr. Qasim Hussein'}
                </h3>
                <p className="text-sm text-[#C9A55A]">
                  {direction === 'rtl' ? 'محامي دولي' : 'International Lawyer'}
                </p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              {t.footer.description}
            </p>
            
            {/* Social Links */}
            <div className={`flex gap-3 ${direction === 'rtl' ? 'justify-end' : ''}`}>
              {[Facebook, Twitter, Linkedin, Instagram].map((Icon, idx) => (
                <a 
                  key={idx}
                  href="#"
                  className="w-10 h-10 bg-white/5 hover:bg-[#C9A55A]/20 border border-white/10 hover:border-[#C9A55A]/50 rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <Icon className="w-5 h-5 text-gray-400 hover:text-[#C9A55A]" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div className={direction === 'rtl' ? 'text-right' : ''}>
            <h4 className="text-white font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#C9A55A] rounded-full" />
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {[
                { label: t.nav.home, id: 'hero' },
                { label: t.nav.about, id: 'about' },
                { label: t.nav.services, id: 'services' },
                { label: t.nav.cases, id: 'cases' },
                { label: t.nav.faq, id: 'faq' },
                { label: t.nav.contact, id: 'contact' }
              ].map((link, idx) => (
                <li key={idx}>
                  <button 
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-[#C9A55A] transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services */}
          <div className={direction === 'rtl' ? 'text-right' : ''}>
            <h4 className="text-white font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#C9A55A] rounded-full" />
              {t.nav.services}
            </h4>
            <ul className="space-y-3">
              {t.services.list.slice(0, 5).map((service, idx) => (
                <li key={idx}>
                  <span className="text-gray-400 hover:text-[#C9A55A] transition-colors cursor-pointer">
                    {service.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div className={direction === 'rtl' ? 'text-right' : ''}>
            <h4 className="text-white font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#C9A55A] rounded-full" />
              {t.contact.info.title}
            </h4>
            <ul className="space-y-4">
              <li className={`flex items-start gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <MapPin className="w-5 h-5 text-[#C9A55A] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">{t.contact.info.address}</span>
              </li>
              <li className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Mail className="w-5 h-5 text-[#C9A55A] flex-shrink-0" />
                <span className="text-gray-400">{t.contact.info.email}</span>
              </li>
              <li className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Phone className="w-5 h-5 text-[#C9A55A] flex-shrink-0" />
                <span className="text-gray-400">{t.contact.info.phone}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-4 ${direction === 'rtl' ? 'md:flex-row-reverse' : ''}`}>
            <p className="text-gray-500 text-sm">
              {t.footer.copyright}
            </p>
            
            <div className={`flex items-center gap-6 text-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <LegalPageDialog type="privacy">
                <button className="text-gray-500 hover:text-[#C9A55A] transition-colors">
                  {t.footer.privacy}
                </button>
              </LegalPageDialog>
              <LegalPageDialog type="terms">
                <button className="text-gray-500 hover:text-[#C9A55A] transition-colors">
                  {t.footer.terms}
                </button>
              </LegalPageDialog>
              <LegalPageDialog type="disclaimer">
                <button className="text-gray-500 hover:text-[#C9A55A] transition-colors">
                  {t.footer.disclaimer}
                </button>
              </LegalPageDialog>
            </div>
          </div>
        </div>
      </div>
      
      {/* Back to Top Button - positioned above toast notifications */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-24 right-8 w-12 h-12 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-[9999]"
        data-testid="back-to-top"
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5 text-[#0A1628]" />
      </button>
    </footer>
  );
};

export default Footer;
